import type { ReactNode } from "react"

import {
  Check,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Pause,
  Play,
  RotateCcw,
  Undo,
} from "lucide-react"
import { useRef, useState } from "react"

import type { PuzzleSummary } from "./types"

import type { TourButtonTarget } from "./gameplay-tour"

import { Button } from "../shared-components/button"

import { RESET_CONFIRM_THRESHOLD } from "./constants"

// When present (challenge / battle mode), the header drops the level index and
// the prev/next puzzle navigation, and shows the live opponent bar + countdown
// instead — collapsing what used to be two headers into one.
export interface BattleInfo {
  // Pre-formatted countdown label (e.g. "00:42" or "waiting…").
  countdownText: string
  // Accept-mode countdown below 10s — the header shows the clock in the
  // destructive color (non-looping urgency cue, puzzle-ui.css).
  countdownUrgent?: boolean
  exitLabel: string
  // Surrender to the creator — accept flow only.
  onConcede?: ()=> void
  onExit: ()=> void
  // Freeze / unfreeze the challenge clock (pauses the timer + shows an overlay).
  onTogglePause?: ()=> void
  opponent?: { hints: number; time: null | number }
  paused?: boolean
  penaltyCount: number
  // Undo-penalty flash text: "+1" in create mode (stopwatch grows by 1s),
  // "-1" in accept mode (countdown shrinks by 1s). Defaults to "+1".
  penaltyLabel?: string
}

export interface PuzzleHeaderProps {
  battle?: BattleInfo
  canHelp?: boolean
  // Normal-play 3-column rails layout: rendered in the header's now-claimed
  // centre (left rail | centre | right rail). Ignored in battle mode, which
  // keeps the flat top header.
  center?: ReactNode
  checkIfSolved: (boardId: string)=> boolean
  disabled?: boolean
  hasNext?: boolean
  hasPrevious?: boolean
  // Sortaballs: shows the growing reveal count ("Show first N correct moves").
  helpMoveCount?: number
  hideHelp?: boolean
  // Header button the guided tour currently points at (steps 7–9) — gets the
  // pulsing `tour-highlight` ring so the player sees which button to watch.
  highlightButton?: TourButtonTarget
  moveCount: number
  onHelp?: ()=> void
  onNext?: ()=> void
  onPrevious?: ()=> void
  onReset?: ()=> void
  onUndo?: ()=> void
  puzzle: PuzzleSummary
  solved: boolean
  totalPuzzles: number
}

export function PuzzleHeader({
  battle,
  canHelp,
  center,
  checkIfSolved,
  disabled,
  hasNext,
  hasPrevious,
  helpMoveCount,
  hideHelp,
  highlightButton,
  moveCount,
  onHelp,
  onNext,
  onPrevious,
  onReset,
  onUndo,
  puzzle,
  solved,
  totalPuzzles,
}: PuzzleHeaderProps) {
  const isAlreadySolved = checkIfSolved(puzzle.boardId)
  // Show the check live as soon as the board is won (solved or near-win), and
  // also when the solve was persisted to localStorage (visible after reload).
  const showSolved = solved || isAlreadySolved
  const solvedTitle = isAlreadySolved ? "Already Solved" : "Solved"

  // A reset discards the player's progress, so once enough moves have been made
  // (RESET_CONFIRM_THRESHOLD) it must be confirmed; a fresh board's reset is
  // harmless and stays one tap. The handler is re-created each render, so it
  // always reads the current `moveCount`; the confirm dialog is a native
  // <dialog> toggled imperatively via its ref — no React state.
  const confirmDialogRef = useRef<HTMLDialogElement>(null)
  const handleResetClick = ()=> {
    if (moveCount >= RESET_CONFIRM_THRESHOLD) {
      confirmDialogRef.current?.showModal()
      return
    }
    onReset?.()
  }

  // The `tour-highlight` class suffix for the button the current tour step
  // teaches — empty string keeps the other buttons untouched.
  const isTourHighlight = (target: TourButtonTarget)=> (highlightButton === target ? " tour-highlight" : "")

  // The help count stays hidden until the player presses help on this puzzle
  // (boardId); a fresh puzzle — or a return to a previously seen one — starts
  // hidden again until its own press. Adjusting during render is React's
  // recommended replacement for a "reset state when a prop changes" effect.
  const [helpTouched, setHelpTouched] = useState(false)
  const [touchedBoardId, setTouchedBoardId] = useState(puzzle.boardId)
  if (touchedBoardId !== puzzle.boardId) {
    setTouchedBoardId(puzzle.boardId)
    setHelpTouched(false)
  }

  // Battle keeps the original flat header untouched. Normal play splits the
  // header into a 3-column rails layout (left status/nav, board centre, right
  // actions), so the board claims the space that used to be an empty centre.
  return (
    <div
      className={`
        puzzle-header
        ${battle ? "" : "puzzle-header--rails"}
      `}
      data-move-count={moveCount}
    >
      {battle ? (
        <>
          <span className="puzzle-header__divider">|</span>
          <div className="flex">
            <button
              className={`puzzle-header__nav-btn puzzle-header__nav-btn--undo mx-4${isTourHighlight("undo")}`}
              disabled={disabled || solved}
              onClick={onUndo}
              title="Undo"
              type="button"
            >
              <Undo size={16} />
            </button>
          </div>
          <div className="flex">
            {!hideHelp && (
              <Button
                className={`puzzle-header__nav-btn--help mx-4 h-9 min-w-9 px-0${isTourHighlight("help")}`}
                disabled={!canHelp || disabled || solved}
                onClick={()=> {
                  setTouchedBoardId(puzzle.boardId)
                  setHelpTouched(true)
                  onHelp?.()
                }}
                title={
                  helpMoveCount === undefined
                    ? "Show correct moves"
                    : `Show first ${helpMoveCount} correct moves`
                }
                type="button"
                variant="transparent"
              >
                <Lightbulb size={16} />
                <span
                  className={`
                    puzzle-header__help-count
                    ${helpTouched ? "" : `puzzle-header__help-count--hidden`}
                  `}
                >
                  {helpMoveCount ?? 5}
                </span>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {battle.opponent && (
              <span className="text-sm text-secondary-foreground">
                Opponent: {battle.opponent.hints} hints ·{" "}
                {battle.opponent.time === null
                  ? "—"
                  : `${battle.opponent.time}s`}
              </span>
            )}
            <span
              className="
                flex items-center gap-1 font-mono text-sm font-semibold text-secondary-foreground
                tabular-nums
              "
            >
              <span
              className={`battle-countdown${battle.countdownUrgent ? " battle-countdown--urgent" : ""}`}
            >
              {battle.countdownText}
            </span>
              <span className="undo-penalty-slot">
                {battle.penaltyCount > 0 && (
                  <span
                    className="undo-penalty-flash"
                    key={battle.penaltyCount}
                  >
                    {battle.penaltyLabel ?? "+1"}
                  </span>
                )}
              </span>
            </span>
            {battle.onConcede && (
              <button
                className="text-xs text-secondary-foreground underline"
                onClick={battle.onConcede}
                type="button"
              >
                Concede
              </button>
            )}
            {battle.onTogglePause && (
              <button
                className="puzzle-header__nav-btn"
                onClick={battle.onTogglePause}
                title={battle.paused ? "Resume" : "Pause"}
                type="button"
              >
                {battle.paused ? <Play size={16} /> : <Pause size={16} />}
              </button>
            )}
            <button
              className="text-xs text-secondary-foreground underline"
              onClick={battle.onExit}
              type="button"
            >
              {battle.exitLabel}
            </button>
          </div>
          <div className="flex">
            <button
              className={`puzzle-header__nav-btn puzzle-header__nav-btn--reset mx-4${isTourHighlight("reset")}`}
              disabled={disabled || solved}
              onClick={handleResetClick}
              title="Reset"
              type="button"
            >
              <RotateCcw size={16} />
            </button>
          </div>
        </>
      ) : (
        <>
          {/* LEFT: status + puzzle navigation. */}
          <div className="puzzle-header__left">
            <span
              className={`
                puzzle-header__solved-check
                ${showSolved ? "visible" : "invisible"}
              `}
              title={solvedTitle}
            >
              <Check size={16} />
            </span>
            <div className="puzzle-header__title">
              #{puzzle.id}/{totalPuzzles}
            </div>
            <div className="flex">
              <button
                className="puzzle-header__nav-btn mx-1"
                disabled={!hasPrevious || disabled}
                onClick={onPrevious}
                title="Previous puzzle"
                type="button"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                className="puzzle-header__nav-btn mx-1"
                disabled={!hasNext || disabled}
                onClick={onNext}
                title="Next puzzle"
                type="button"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* CENTRE: the game board lives here in the 3-column rails layout —
              the space that used to be deliberately empty above the board. */}

          {center ? (
            <div className="puzzle-header__center">{center}</div>
          ) : null}

          {/* RIGHT: undo, reset and help together (undo + reset neighbours,
              help — the lightbulb — last). */}
          <div className="puzzle-header__right">
            <button
              className={`puzzle-header__nav-btn puzzle-header__nav-btn--undo${isTourHighlight("undo")}`}
              disabled={disabled || solved}
              onClick={onUndo}
              title="Undo"
              type="button"
            >
              <Undo size={16} />
            </button>
            <button
              className={`puzzle-header__nav-btn puzzle-header__nav-btn--reset${isTourHighlight("reset")}`}
              disabled={disabled || solved}
              onClick={handleResetClick}
              title="Reset"
              type="button"
            >
              <RotateCcw size={16} />
            </button>
            {!hideHelp && (
              <Button
                className={`puzzle-header__nav-btn--help h-9 min-w-9 px-0${isTourHighlight("help")}`}
                disabled={!canHelp || disabled || solved}
                onClick={()=> {
                  setTouchedBoardId(puzzle.boardId)
                  setHelpTouched(true)
                  onHelp?.()
                }}
                title={
                  helpMoveCount === undefined
                    ? "Show correct moves"
                    : `Show first ${helpMoveCount} correct moves`
                }
                type="button"
                variant="transparent"
              >
                <Lightbulb size={16} />
                <span
                  className={`
                    puzzle-header__help-count
                    ${helpTouched ? "" : `puzzle-header__help-count--hidden`}
                  `}
                >
                  {helpMoveCount ?? 5}
                </span>
              </Button>
            )}
          </div>
        </>
      )}
      <dialog
        aria-label="Reset the board?"
        aria-modal="true"
        className="sortaballs-reset-confirm"
        onClick={event=> {
          // Clicking the backdrop (the dialog element itself, outside the box)
          // cancels without resetting.
          if (event.target === event.currentTarget) {
            confirmDialogRef.current?.close()
          }
        }}
        ref={confirmDialogRef}
      >
        <div className="sortaballs-reset-confirm__box">
          <p className="sortaballs-reset-confirm__text">
            You've made {moveCount} moves. Reset and start over?
          </p>
          <div className="sortaballs-reset-confirm__actions">
            <button
              className="sortaballs-reset-confirm__btn"
              onClick={()=> confirmDialogRef.current?.close()}
              type="button"
            >
              Cancel
            </button>
            <button
              className="sortaballs-reset-confirm__btn sortaballs-reset-confirm__btn--danger"
              onClick={()=> {
                confirmDialogRef.current?.close()
                onReset?.()
              }}
              type="button"
            >
              Confirm
            </button>
          </div>
        </div>
      </dialog>
    </div>
  )
}
